import { useEffect, useState } from 'react';
import { Eye, EyeOff, Loader2, Save, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { aiApi } from '@/services/aiApi';

export function AISettingsPage() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [providerBaseUrl, setProviderBaseUrl] = useState('https://api.openai.com/v1');
  const [modelName, setModelName] = useState('gpt-4o-mini');
  const [embeddingModelName, setEmbeddingModelName] = useState('text-embedding-3-small');
  const [apiKey, setApiKey] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [customHeaders, setCustomHeaders] = useState<Record<string, string>>({});
  const [headerKey, setHeaderKey] = useState('');
  const [headerValue, setHeaderValue] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [systemEnabled, setSystemEnabled] = useState(false);
  const [canConfigureUserProvider, setCanConfigureUserProvider] = useState(false);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const { data: response } = await aiApi.getUserAIConfig();
      const config = response.data;
      setProviderBaseUrl(config.providerBaseUrl);
      setModelName(config.modelName);
      setEmbeddingModelName(config.embeddingModelName);
      setIsEnabled(config.userProviderEnabled);
      setHasApiKey(config.hasApiKey);
      setCustomHeaders(config.customHeaders || {});
      setSystemEnabled(config.systemEnabled);
      setCanConfigureUserProvider(config.canConfigureUserProvider);
    } catch {
      toast({ title: '获取 AI 配置失败', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: {
        providerBaseUrl: string;
        modelName: string;
        embeddingModelName: string;
        isEnabled: boolean;
        customHeaders: Record<string, string>;
        apiKey?: string;
      } = {
        providerBaseUrl,
        modelName,
        embeddingModelName,
        isEnabled,
        customHeaders,
      };
      if (apiKey.trim()) {
        payload.apiKey = apiKey.trim();
      }
      await aiApi.updateUserAIConfig(payload);
      setApiKey('');
      await loadConfig();
      toast({ title: 'AI 配置已保存' });
    } catch {
      toast({ title: '保存失败', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!canConfigureUserProvider) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">AI 设置</h1>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              平台统一 AI
            </CardTitle>
            <CardDescription>
              {systemEnabled
                ? 'AI 能力由平台统一提供，写笔记时可直接使用，无需配置个人 API Key。'
                : '平台 AI 服务暂未启用，请联系管理员完成统一 Provider 配置。'}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">AI 设置</h1>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            个人 AI Provider
          </CardTitle>
          <CardDescription>
            配置个人 OpenAI 兼容 Provider。仅在平台未启用统一 AI 或管理员已允许覆盖时生效。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="ai-enabled">启用个人 Provider</Label>
              <p className="text-xs text-gray-500">启用后，个人配置会在允许覆盖的平台策略下优先使用。</p>
            </div>
            <Switch id="ai-enabled" checked={isEnabled} onCheckedChange={setIsEnabled} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="provider-url">Provider Base URL</Label>
            <Input
              id="provider-url"
              value={providerBaseUrl}
              onChange={(event) => setProviderBaseUrl(event.target.value)}
              placeholder="https://api.openai.com/v1"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="model-name">模型名称</Label>
            <Input
              id="model-name"
              value={modelName}
              onChange={(event) => setModelName(event.target.value)}
              placeholder="gpt-4o-mini"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="embedding-model-name">Embedding 模型名称</Label>
            <Input
              id="embedding-model-name"
              value={embeddingModelName}
              onChange={(event) => setEmbeddingModelName(event.target.value)}
              placeholder="text-embedding-3-small"
            />
            <p className="text-xs text-gray-500">用于笔记语义检索；Provider 不支持时请填写兼容模型。</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="api-key">API Key{hasApiKey ? '（已保存，可留空）' : ''}</Label>
            <div className="relative">
              <Input
                id="api-key"
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(event) => setApiKey(event.target.value)}
                placeholder={hasApiKey ? '留空以保留当前 Key' : 'sk-...'}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowKey((value) => !value)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label={showKey ? '隐藏 API Key' : '显示 API Key'}
              >
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>自定义请求头</Label>
            <div className="space-y-2">
              {Object.entries(customHeaders).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2 text-sm">
                  <span className="rounded bg-gray-100 px-2 py-1 font-mono text-gray-700">{key}</span>
                  <span className="text-gray-500">=</span>
                  <span className="flex-1 truncate font-mono text-gray-700">{value}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const nextHeaders = { ...customHeaders };
                      delete nextHeaders[key];
                      setCustomHeaders(nextHeaders);
                    }}
                    className="text-gray-400 hover:text-red-500"
                    aria-label={`删除 ${key}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={headerKey} onChange={(event) => setHeaderKey(event.target.value)} placeholder="Header 名称" />
              <Input value={headerValue} onChange={(event) => setHeaderValue(event.target.value)} placeholder="Header 值" />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!headerKey.trim() || !headerValue.trim()) return;
                  setCustomHeaders({ ...customHeaders, [headerKey.trim()]: headerValue.trim() });
                  setHeaderKey('');
                  setHeaderValue('');
                }}
              >
                添加
              </Button>
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            保存配置
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}